/**
 * Neyra — mock social features (saves, follows, interested, chat, notifications).
 * All localStorage. Reactive via a tiny custom event bus so multiple components
 * re-render when a store changes.
 */
import { mirror } from "@/lib/syncStore";
import { SUPABASE_TABLES } from "@/lib/supabaseClient";

const fireMirror = (table, row) => { try { mirror(table, row); } catch {} };

const BUS_EVT = "neara-social-change";
const emit = () => window.dispatchEvent(new Event(BUS_EVT));

export const subscribeSocial = (cb) => {
    window.addEventListener(BUS_EVT, cb);
    return () => window.removeEventListener(BUS_EVT, cb);
};

const read = (k, fallback) => {
    try {
        const v = localStorage.getItem(k);
        return v ? JSON.parse(v) : fallback;
    } catch {
        return fallback;
    }
};
const write = (k, v) => {
    localStorage.setItem(k, JSON.stringify(v));
    emit();
};

const userKey = (userId, suffix) => `neara.${suffix}.${userId}`;

/* ---------------- Saved items ---------------- */

const asArray = (v) => (Array.isArray(v) ? v : []);

export const getSaved = (userId) => {
    const raw = read(userKey(userId, "saved"), null);
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        return {
            services: asArray(raw.services),
            events: asArray(raw.events),
        };
    }
    return { services: [], events: [] };
};

/** Convenience — flat array of saved service IDs (safe even if data is malformed). */
export const getSavedServiceIds = (userId) => asArray(getSaved(userId).services);
export const getSavedEventIds   = (userId) => asArray(getSaved(userId).events);

export const toggleSaveService = (userId, serviceId) => {
    const cur = getSaved(userId);
    const set = new Set(cur.services);
    set.has(serviceId) ? set.delete(serviceId) : set.add(serviceId);
    write(userKey(userId, "saved"), { ...cur, services: [...set] });
};

export const toggleSaveEvent = (userId, eventId) => {
    const cur = getSaved(userId);
    const set = new Set(cur.events);
    set.has(eventId) ? set.delete(eventId) : set.add(eventId);
    write(userKey(userId, "saved"), { ...cur, events: [...set] });
};

export const isServiceSaved = (userId, serviceId) =>
    getSaved(userId).services.includes(serviceId);

export const isEventSaved = (userId, eventId) =>
    getSaved(userId).events.includes(eventId);

/* ---------------- Interested events ---------------- */

export const getInterestedEvents = (userId) =>
    asArray(read(userKey(userId, "interested"), []));

export const toggleInterested = (userId, eventId) => {
    const cur = new Set(getInterestedEvents(userId));
    cur.has(eventId) ? cur.delete(eventId) : cur.add(eventId);
    write(userKey(userId, "interested"), [...cur]);
};

export const isInterested = (userId, eventId) =>
    getInterestedEvents(userId).includes(eventId);

/* ---------------- Event joining ---------------- */

export const getJoinedEvents = (userId) => asArray(read(userKey(userId, "joined"), []));

export const isEventJoined = (userId, eventId) =>
    getJoinedEvents(userId).includes(eventId);

export const joinEvent = (userId, eventId) => {
    const cur = new Set(getJoinedEvents(userId));
    if (cur.has(eventId)) return;
    cur.add(eventId);
    write(userKey(userId, "joined"), [...cur]);
    // Also mark as interested for continuity with older UI state
    const interested = new Set(getInterestedEvents(userId));
    interested.add(eventId);
    write(userKey(userId, "interested"), [...interested]);
};

/* ---------------- Event group chat (per event, across users) ---------------- */

const eventChatKey = (eventId) => `neara.eventChat.${eventId}`;

export const getEventMessages = (eventId) => read(eventChatKey(eventId), []);

export const seedEventChatOnce = (_eventId, _event) => {
    // Phase 1 refinement: removed all fake demo messages — chat starts empty.
};

export const sendEventMessage = (eventId, user, text) => {
    const clean = text.trim();
    if (!clean) return;
    const arr = getEventMessages(eventId);
    arr.push({
        id: Date.now() + "_" + Math.random().toString(36).slice(2, 6),
        from: user.id,
        fromName: user.fullName,
        text: clean,
        at: Date.now(),
    });
    localStorage.setItem(eventChatKey(eventId), JSON.stringify(arr));
    emit();
};

/* ---------------- 1:1 conversations: get or create with a peer ---------------- */

export const getOrCreateConversation = (userId, peer) => {
    const list = getConversations(userId);
    const convId = "c_" + (peer.id || peer.username || "peer");
    const existing = list.find((c) => c.id === convId);
    if (existing) return existing.id;
    const now = Date.now();
    const conv = {
        id: convId,
        peerId: peer.id || peer.username,
        peerName: peer.name || peer.fullName || "User",
        peerUsername: peer.username || "user",
        lastMessage: "Say hi 👋",
        updatedAt: now,
        unread: 0,
    };
    list.unshift(conv);
    saveConversations(userId, list);
    // No welcome seed — chat starts empty.
    const msgs = getMessages(userId);
    if (!msgs[convId]) {
        msgs[convId] = [];
        saveMessages(userId, msgs);
    }
    return convId;
};

/* ---------------- Conversations & Messages ---------------- */

const convKey = (userId) => userKey(userId, "conversations");
const msgsKey = (userId) => userKey(userId, "messages");
const reqKey  = (userId) => userKey(userId, "requests");
const muteKey = (userId) => userKey(userId, "mutes"); // { direct: [convId], event: [eventId] }
const followKey = (userId) => userKey(userId, "following"); // [peerId]
const violationKey = (userId, peerId) => userKey(userId, `violations.${peerId}`);

export const getConversations = (userId) => asArray(read(convKey(userId), []));
export const getMessages = (userId) => {
    const raw = read(msgsKey(userId), {});
    return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
};

const saveConversations = (userId, list) => write(convKey(userId), list);
const saveMessages = (userId, map) => write(msgsKey(userId), map);

export const getConversation = (userId, convId) =>
    getConversations(userId).find((c) => c.id === convId) || null;

export const markConversationRead = (userId, convId) => {
    const list = getConversations(userId);
    const idx = list.findIndex((c) => c.id === convId);
    if (idx < 0) return;
    if (list[idx].unread > 0) {
        list[idx] = { ...list[idx], unread: 0 };
        saveConversations(userId, list);
    }
};

export const totalUnread = (userId) =>
    getConversations(userId).reduce((s, c) => s + (c.unread || 0), 0);

export const sendMessage = (userId, convId, text) => {
    const clean = text.trim();
    if (!clean) return;
    const map = getMessages(userId);
    const arr = map[convId] || [];
    const msg = { id: Date.now() + "_" + Math.random().toString(36).slice(2, 6), from: "me", text: clean, at: Date.now() };
    map[convId] = [...arr, msg];
    saveMessages(userId, map);
    fireMirror(SUPABASE_TABLES.MESSAGES, { ...msg, conversationId: convId, fromUserId: userId });

    const list = getConversations(userId);
    const idx = list.findIndex((c) => c.id === convId);
    if (idx >= 0) {
        list[idx] = { ...list[idx], lastMessage: clean, updatedAt: Date.now(), unread: 0 };
        const conv = list.splice(idx, 1)[0];
        list.unshift(conv);
        saveConversations(userId, list);
    }
};

const REPLIES = [];

const simulateReply = (_userId, _convId) => {
    // Phase 1 refinement: removed all fake auto-replies.
};

/* ---------------- Notifications ---------------- */

const notifKey = (userId) => userKey(userId, "notifications");

export const getNotifications = (userId) => asArray(read(notifKey(userId), []));

export const unreadNotifCount = (userId) =>
    getNotifications(userId).filter((n) => !n.read).length;

export const markNotifRead = (userId, id) => {
    const list = getNotifications(userId).map((n) =>
        n.id === id ? { ...n, read: true } : n
    );
    write(notifKey(userId), list);
};

export const markAllNotifsRead = (userId) => {
    const list = getNotifications(userId).map((n) => ({ ...n, read: true }));
    write(notifKey(userId), list);
};

export const addNotification = (userId, notif) => {
    const list = getNotifications(userId);
    const record = { id: "n_" + Date.now(), read: false, at: Date.now(), userId, ...notif };
    list.unshift(record);
    write(notifKey(userId), list);
    fireMirror(SUPABASE_TABLES.NOTIFICATIONS, record);
};

/* ---------------- Seed for new user ---------------- */

const SEED_FLAG = (userId) => userKey(userId, "seeded");

export const seedUserDataOnce = (_userId, _people) => {
    // Phase 1 refinement: removed all fake demo conversations / notifications.
    // New accounts now start with truly empty inbox and notifications.
};

/* ---------------- Connections (people followed/known) ---------------- */

export const getFollowing = (userId) => asArray(read(followKey(userId), []));

/** Reverse lookup — who is following the given user. */
export const getFollowers = (userId) => {
    if (!userId) return [];
    const out = [];
    const prefix = "neara.following.";
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (!k || !k.startsWith(prefix)) continue;
            const ownerId = k.slice(prefix.length);
            if (ownerId === userId) continue;
            const list = read(k, []);
            if (Array.isArray(list) && list.includes(userId)) out.push(ownerId);
        }
    } catch {}
    return out;
};

export const isFollowing = (userId, peerId) =>
    getFollowing(userId).includes(peerId);

export const toggleFollow = (userId, peerId) => {
    const list = new Set(getFollowing(userId));
    const wasFollowing = list.has(peerId);
    if (wasFollowing) list.delete(peerId);
    else list.add(peerId);
    write(followKey(userId), [...list]);
    fireMirror(SUPABASE_TABLES.FOLLOWERS, {
        id: `${userId}_${peerId}`,
        followerId: userId,
        followedId: peerId,
        following: !wasFollowing,
        updatedAt: new Date().toISOString(),
    });

    // Notify target of a new follower (best-effort; target may not be a real local user)
    if (!wasFollowing) {
        try {
            const usersRaw = localStorage.getItem("neara.users");
            if (usersRaw) {
                const users = JSON.parse(usersRaw);
                const me = users.find((u) => u.id === userId);
                const target = users.find((u) => u.id === peerId);
                if (target) {
                    addNotification(target.id, {
                        type: "follower",
                        title: "New follower",
                        body: `@${me?.username || "someone"} started following you.`,
                        link: me?.username ? `/u/${me.username}` : "/profile",
                    });
                }
            }
        } catch {}
    }
};

/**
 * "Connected" means: existing conversation OR the user follows them.
 * Used to decide if a new message goes to inbox or requests.
 */
export const areConnected = (userId, peerId) => {
    if (isFollowing(userId, peerId)) return true;
    const convs = getConversations(userId);
    return convs.some(
        (c) => c.peerId === peerId && (c.from !== "request" || c.acceptedAt)
    );
};

/* ---------------- Message Requests ---------------- */

export const getRequests = (userId) => read(reqKey(userId), []);

export const addRequest = (userId, peer, firstMessage) => {
    const list = getRequests(userId);
    const id = "rq_" + (peer.id || peer.username);
    if (list.some((r) => r.id === id)) return id;
    list.unshift({
        id,
        peerId: peer.id || peer.username,
        peerName: peer.name || peer.fullName,
        peerUsername: peer.username,
        message: firstMessage || "Wants to connect",
        at: Date.now(),
    });
    write(reqKey(userId), list);
    return id;
};

export const acceptRequest = (userId, requestId) => {
    const list = getRequests(userId);
    const req = list.find((r) => r.id === requestId);
    if (!req) return null;
    write(reqKey(userId), list.filter((r) => r.id !== requestId));
    // Promote to a real conversation
    const peer = { id: req.peerId, name: req.peerName, username: req.peerUsername };
    const convId = getOrCreateConversation(userId, peer);
    return convId;
};

export const declineRequest = (userId, requestId) => {
    const list = getRequests(userId).filter((r) => r.id !== requestId);
    write(reqKey(userId), list);
};

/* ---------------- Mute ---------------- */

const getMutes = (userId) => read(muteKey(userId), { direct: [], event: [] });

export const isMuted = (userId, kind, id) => {
    const m = getMutes(userId);
    return (m[kind] || []).includes(id);
};

export const toggleMute = (userId, kind, id) => {
    const m = getMutes(userId);
    const set = new Set(m[kind] || []);
    set.has(id) ? set.delete(id) : set.add(id);
    write(muteKey(userId), { ...m, [kind]: [...set] });
};

/* ---------------- Online indicator (mock) ---------------- */

/** Deterministic pseudo-online flag from peerId — ~60% of peers feel "online". */
export const isPeerOnline = (peerId) => {
    const s = String(peerId || "");
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return h % 5 < 3;
};

/* ---------------- Chat protection (phone / whatsapp detection) ---------------- */

const PHONE_RE = /(?:\+?\d[\s\-().]*){8,}/;
const WA_RE = /\b(?:wa\.me|api\.whatsapp\.com|chat\.whatsapp\.com|whatsapp(?:\s|:|\.)?(?:me|number)?)\b/i;
const WORD_RE = /\b(whatsapp|whats app|wassap|wsap|telegram|signal|snapchat|insta(?:gram)?(?:\s)?dm)\b/i;

export const detectUnsafeContent = (text) => {
    if (!text) return null;
    if (PHONE_RE.test(text)) return "phone";
    if (WA_RE.test(text) || WORD_RE.test(text)) return "external";
    return null;
};

export const bumpViolations = (userId, peerId) => {
    const cur = read(violationKey(userId, peerId), 0);
    const next = cur + 1;
    localStorage.setItem(violationKey(userId, peerId), JSON.stringify(next));
    return next;
};

export const getViolations = (userId, peerId) =>
    read(violationKey(userId, peerId), 0);

